import { useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Inbox, Images, Columns2, Star, Settings, LogOut, HardHat, Globe, Wrench } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api, imgUrl } from "../lib/api";
import { Seo } from "../components/Seo";

const LINKS = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/leads", label: "Estimate Requests", icon: Inbox },
  { to: "/portal/gallery", label: "Manage Projects", icon: Images },
  { to: "/portal/before-after", label: "Before & After", icon: Columns2 },
  { to: "/portal/services", label: "Manage Services", icon: Wrench },
  { to: "/portal/reviews", label: "Reviews", icon: Star },
  { to: "/portal/settings", label: "Settings", icon: Settings },
];

export default function PortalLayout() {
  const { user, setUser, settings } = useSite();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === null) navigate("/portal/login");
  }, [user, navigate]);

  if (user === undefined || user === null) {
    return <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center text-slate-400 font-heading">Loading…</div>;
  }

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex" data-testid="portal-layout">
      <Seo noindex title="Owner Portal | Noyola Construction LLC" path="/portal" />
      <aside className="w-16 md:w-64 bg-[#0B1B3D] flex flex-col shrink-0">
        <div className="p-4 md:p-6 flex items-center gap-3 border-b border-white/10">
          {settings.logo_url ? (
            <span className="bg-white px-2 py-1.5 rounded-sm inline-flex items-center mx-auto md:mx-0">
              <img src={imgUrl(settings.logo_url)} alt="Noyola Construction LLC" className="h-8 w-auto object-contain" />
            </span>
          ) : (
            <>
              <span className="w-9 h-9 bg-[#005AE0] flex items-center justify-center shrink-0"><HardHat className="w-4 h-4 text-white" /></span>
              <div className="hidden md:block font-heading text-white">
                <p className="font-extrabold text-sm leading-none">NOYOLA</p>
                <p className="text-[9px] tracking-[0.25em] opacity-60">BUSINESS PORTAL</p>
              </div>
            </>
          )}
        </div>
        <nav className="flex-1 py-4">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`portal-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 md:px-6 py-3.5 text-sm font-heading font-semibold transition-colors ${
                  isActive ? "bg-[#005AE0] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <l.icon className="w-5 h-5 shrink-0" />
              <span className="hidden md:inline">{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-2 py-2.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="portal-view-site">
            <Globe className="w-4 h-4 shrink-0" /><span className="hidden md:inline">View Website</span>
          </Link>
          <button onClick={logout} data-testid="portal-logout-btn" className="flex items-center gap-3 px-2 py-2.5 text-sm text-slate-400 hover:text-white transition-colors w-full">
            <LogOut className="w-4 h-4 shrink-0" /><span className="hidden md:inline">Sign Out</span>
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="font-heading font-bold text-[#0B1B3D]">Business Dashboard</p>
          <p className="text-sm text-slate-500">{user.name} <span className="text-xs bg-slate-100 px-2 py-1 ml-1 uppercase font-heading font-bold text-slate-500">{user.role.replace("_", " ")}</span></p>
        </header>
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
